/**
 * Co-Pilot Page
 * 
 * AI-powered assistant that knows everything about your customers and business.
 * Features a ChatGPT-like interface with conversation history, copy/share functionality,
 * and beautiful, organized responses.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import DOMPurify from 'dompurify';
import useAuthenticatedAxios from '../auth/AuthenticatedAxios';
import { useToast } from '../Context/ToastContext';
import CoPilotIcon from '../Images/copilot.svg';
import { BsClipboard, BsCheck2, BsArrowClockwise, BsTrash, BsPlusLg, BsChatDots, BsSearch, BsChevronDown, BsLayoutSidebarReverse } from 'react-icons/bs';
import ActivityLog from '../Components/ActivityLog';

// ============================================
// ANIMATIONS
// ============================================

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(-20px); }
  to { opacity: 1; transform: translateX(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const typingDots = keyframes`
  0%, 20% { opacity: 0.3; }
  50% { opacity: 1; }
  80%, 100% { opacity: 0.3; }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

// ============================================
// STYLED COMPONENTS - LAYOUT
// ============================================

const PageContainer = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  background: linear-gradient(135deg, #0a0e17 0%, #0f1629 50%, #0a0e17 100%);
  overflow: hidden;
`;

const Sidebar = styled.div`
  width: ${props => props.$collapsed ? '0' : '280px'};
  min-width: ${props => props.$collapsed ? '0' : '280px'};
  background: linear-gradient(180deg, rgba(15, 22, 41, 0.95) 0%, rgba(10, 14, 23, 0.98) 100%);
  border-right: 1px solid rgba(99, 102, 241, 0.15);
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  overflow: hidden;
`;

const SidebarHeader = styled.div`
  padding: 20px 16px;
  border-bottom: 1px solid rgba(99, 102, 241, 0.1);
`;

const NewChatButton = styled.button`
  width: 100%;
  padding: 12px 16px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.1) 100%);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 10px;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s ease;
  
  &:hover {
    background: linear-gradient(135deg, rgba(99, 102, 241, 0.25) 0%, rgba(139, 92, 246, 0.2) 100%);
    border-color: rgba(99, 102, 241, 0.5);
    transform: translateY(-1px);
  }
  
  svg {
    font-size: 16px;
  }
`;

const ConversationList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px 8px;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.3);
    border-radius: 2px;
  }
`;

const ConversationGroup = styled.div`
  margin-bottom: 16px;
`;

const GroupLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: rgba(165, 180, 252, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 8px 12px 4px;
`;

const ConversationItem = styled.div`
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${props => props.$active ? '#fff' : 'rgba(255, 255, 255, 0.7)'};
  background: ${props => props.$active ? 'rgba(99, 102, 241, 0.15)' : 'transparent'};
  border: 1px solid ${props => props.$active ? 'rgba(99, 102, 241, 0.3)' : 'transparent'};
  transition: all 0.15s ease;
  animation: ${slideIn} 0.3s ease-out;
  
  &:hover {
    background: rgba(99, 102, 241, 0.1);
  }
  
  .icon {
    flex-shrink: 0;
    color: rgba(165, 180, 252, 0.6);
  }
  
  .title {
    flex: 1;
    font-size: 13px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .delete-btn {
    opacity: 0;
    color: rgba(255, 100, 100, 0.7);
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    transition: all 0.15s ease;
    
    &:hover {
      color: #ff6b6b;
      background: rgba(255, 100, 100, 0.1);
    }
  }
  
  &:hover .delete-btn {
    opacity: 1;
  }
`;

// ============================================
// STYLED COMPONENTS - MAIN CHAT AREA
// ============================================

const MainArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ActivityLogPanel = styled.div`
  width: ${props => props.$collapsed ? '0' : '350px'};
  min-width: ${props => props.$collapsed ? '0' : '350px'};
  transition: all 0.3s ease;
  overflow: hidden;
`;

const ToggleLogButton = styled.button`
  padding: 8px 12px;
  background: ${props => props.$active ? 'rgba(139, 92, 246, 0.2)' : 'rgba(99, 102, 241, 0.1)'};
  border: 1px solid ${props => props.$active ? 'rgba(139, 92, 246, 0.4)' : 'rgba(99, 102, 241, 0.2)'};
  border-radius: 8px;
  color: ${props => props.$active ? '#a78bfa' : 'rgba(165, 180, 252, 0.7)'};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(139, 92, 246, 0.2);
    border-color: rgba(139, 92, 246, 0.4);
    color: #a78bfa;
  }
  
  svg {
    font-size: 14px;
  }
`;

const ChatHeader = styled.div`
  padding: 16px 24px;
  background: rgba(15, 22, 41, 0.6);
  border-bottom: 1px solid rgba(99, 102, 241, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  
  .icon-wrapper {
    width: 40px;
    height: 40px;
    background: linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.15) 100%);
    border: 1px solid rgba(99, 102, 241, 0.3);
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    img {
      width: 24px;
      height: 24px;
      filter: brightness(0) invert(1);
    }
  }
  
  .title-text {
    h2 {
      font-size: 18px;
      font-weight: 600;
      color: #fff;
      margin: 0;
    }
    
    span {
      font-size: 12px;
      color: rgba(165, 180, 252, 0.6);
    }
  }
`;

const MessagesArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.3);
    border-radius: 3px;
  }
`;

const WelcomeContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px 20px;
  animation: ${fadeIn} 0.5s ease-out;
`;

const WelcomeIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.1) 100%);
  border: 2px solid rgba(99, 102, 241, 0.3);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  
  img {
    width: 48px;
    height: 48px;
    filter: brightness(0) invert(1);
  }
`;

const WelcomeTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 12px 0;
  
  span {
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const WelcomeSubtitle = styled.p`
  font-size: 15px;
  color: rgba(255, 255, 255, 0.6);
  max-width: 500px;
  line-height: 1.6;
  margin: 0 0 40px 0;
`;

const SuggestionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  max-width: 700px;
  width: 100%;
`;

const SuggestionCard = styled.button`
  padding: 16px 20px;
  background: rgba(15, 22, 41, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 12px;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(99, 102, 241, 0.1);
    border-color: rgba(99, 102, 241, 0.4);
    transform: translateY(-2px);
  }
  
  .title {
    font-size: 14px;
    font-weight: 600;
    color: #fff;
    margin-bottom: 6px;
  }
  
  .description {
    font-size: 12px;
    color: rgba(165, 180, 252, 0.6);
    line-height: 1.4;
  }
`;

// ============================================
// STYLED COMPONENTS - MESSAGES
// ============================================

const MessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  animation: ${fadeIn} 0.3s ease-out;
  max-width: 900px;
  width: 100%;
  margin: 0 auto;
`;

const MessageRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
  
  ${props => props.$isUser && `
    flex-direction: row-reverse;
  `}
`;

const Avatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  
  ${props => props.$isUser ? `
    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
    color: #fff;
    font-weight: 600;
    font-size: 14px;
  ` : `
    background: linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.15) 100%);
    border: 1px solid rgba(99, 102, 241, 0.3);
    
    img {
      width: 22px;
      height: 22px;
      filter: brightness(0) invert(1);
    }
  `}
`;

const MessageContent = styled.div`
  flex: 1;
  max-width: 95%;
  min-width: 0;
`;

const MessageBubble = styled.div`
  padding: 16px 20px;
  border-radius: 16px;
  font-size: 14px;
  line-height: 1.7;
  overflow: hidden;
  
  ${props => props.$isUser ? `
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(99, 102, 241, 0.15) 100%);
    border: 1px solid rgba(59, 130, 246, 0.3);
    color: rgba(255, 255, 255, 0.95);
    border-bottom-right-radius: 4px;
  ` : `
    background: rgba(20, 25, 40, 0.9);
    border: 1px solid rgba(100, 150, 200, 0.1);
    color: rgba(255, 255, 255, 0.85);
    border-bottom-left-radius: 4px;
  `}
  
  /* Markdown styling */
  strong, b {
    color: #a5b4fc;
    font-weight: 600;
  }
  
  code {
    background: rgba(99, 102, 241, 0.15);
    padding: 2px 6px;
    border-radius: 4px;
    font-family: 'JetBrains Mono', 'Consolas', monospace;
    font-size: 13px;
    color: #c4b5fd;
  }
  
  pre {
    background: rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(99, 102, 241, 0.2);
    border-radius: 8px;
    padding: 16px;
    overflow-x: auto;
    margin: 12px 0;
    
    code {
      background: none;
      padding: 0;
    }
  }
  
  ul, ol {
    margin: 12px 0;
    padding-left: 0;
  }
  
  li {
    margin: 8px 0;
    padding-left: 24px;
    position: relative;
    
    &::before {
      position: absolute;
      left: 0;
    }
  }
  
  ul li::before {
    content: '•';
    color: #6366f1;
    font-weight: bold;
  }
  
  ol {
    counter-reset: item;
  }
  
  ol li {
    counter-increment: item;
    
    &::before {
      content: counter(item) '.';
      color: #a5b4fc;
      font-weight: 600;
    }
  }
  
  p {
    margin: 10px 0;
    
    &:first-child { margin-top: 0; }
    &:last-child { margin-bottom: 0; }
  }
  
  table {
    width: 100%;
    border-collapse: collapse;
    margin: 16px 0;
    font-size: 13px;
    display: block;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    
    thead, tbody, tr, th, td {
      display: revert;
    }
    
    th, td {
      padding: 8px 10px;
      border: 1px solid rgba(99, 102, 241, 0.2);
      text-align: left;
      white-space: nowrap;
      max-width: 220px;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    
    td:first-child, td:nth-child(2) {
      white-space: normal;
      word-break: break-word;
    }
    
    th {
      background: rgba(99, 102, 241, 0.1);
      color: #a5b4fc;
      font-weight: 600;
      position: sticky;
      top: 0;
    }
    
    tr:nth-child(even) {
      background: rgba(0, 0, 0, 0.15);
    }
    
    &::-webkit-scrollbar {
      height: 6px;
    }
    
    &::-webkit-scrollbar-track {
      background: rgba(0, 0, 0, 0.1);
      border-radius: 3px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: rgba(99, 102, 241, 0.3);
      border-radius: 3px;
    }
  }
  
  .md-h1 {
    font-size: 20px;
    font-weight: 700;
    color: #fff;
    margin: 20px 0 12px 0;
    padding-bottom: 8px;
    border-bottom: 1px solid rgba(99, 102, 241, 0.2);
    
    &:first-child { margin-top: 0; }
  }
  
  .md-h2 {
    font-size: 16px;
    font-weight: 600;
    color: #e0e7ff;
    margin: 16px 0 10px 0;
    
    &:first-child { margin-top: 0; }
  }
  
  .md-h3 {
    font-size: 14px;
    font-weight: 600;
    color: #a5b4fc;
    margin: 14px 0 8px 0;
    
    &:first-child { margin-top: 0; }
  }
`;

const MessageActions = styled.div`
  display: flex;
  gap: 4px;
  margin-top: 8px;
  opacity: 0;
  transition: opacity 0.2s ease;
  
  ${MessageContainer}:hover & {
    opacity: 1;
  }
`;

const ActionButton = styled.button`
  padding: 6px 10px;
  background: rgba(99, 102, 241, 0.1);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 6px;
  color: rgba(165, 180, 252, 0.7);
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.15s ease;
  
  &:hover {
    background: rgba(99, 102, 241, 0.2);
    border-color: rgba(99, 102, 241, 0.4);
    color: #fff;
  }
  
  &.copied {
    background: rgba(16, 185, 129, 0.2);
    border-color: rgba(16, 185, 129, 0.4);
    color: #10b981;
  }
`;

const TypingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 16px 20px;
  background: rgba(20, 25, 40, 0.9);
  border: 1px solid rgba(100, 150, 200, 0.1);
  border-radius: 16px;
  border-bottom-left-radius: 4px;
  max-width: 100px;
  
  span {
    width: 8px;
    height: 8px;
    background: #a5b4fc;
    border-radius: 50%;
    animation: ${typingDots} 1.4s infinite;
    
    &:nth-child(2) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.4s; }
  }
`;

// ============================================
// STYLED COMPONENTS - INPUT AREA
// ============================================

const InputArea = styled.div`
  padding: 20px 24px 24px;
  background: linear-gradient(180deg, transparent 0%, rgba(15, 22, 41, 0.8) 100%);
`;

const InputContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  background: rgba(20, 28, 50, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.25);
  border-radius: 16px;
  padding: 4px;
  transition: all 0.2s ease;
  
  &:focus-within {
    border-color: rgba(99, 102, 241, 0.5);
    box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1);
  }
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 8px 12px;
`;

const TextArea = styled.textarea`
  flex: 1;
  background: transparent;
  border: none;
  color: #fff;
  font-size: 15px;
  font-family: inherit;
  resize: none;
  min-height: 24px;
  max-height: 200px;
  line-height: 1.5;
  padding: 4px 0;
  
  &:focus {
    outline: none;
  }
  
  &::placeholder {
    color: rgba(165, 180, 252, 0.4);
  }
`;

const SendButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: none;
  background: ${props => props.disabled ? 'rgba(99, 102, 241, 0.2)' : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'};
  color: #fff;
  font-size: 18px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
  
  &:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: 0 4px 20px rgba(99, 102, 241, 0.4);
  }
`;

const InputHint = styled.div`
  text-align: center;
  font-size: 11px;
  color: rgba(165, 180, 252, 0.4);
  margin-top: 12px;
`;

// ============================================
// HELPER FUNCTIONS
// ============================================

const parseMarkdown = (text) => {
  if (!text) return '';
  
  let html = text;
  
  // Code blocks
  html = html.replace(/```(\w+)?\n([\s\S]*?)```/g, '<pre><code>$2</code></pre>');
  
  // Inline code
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
  
  // Headers
  html = html.replace(/^### (.+)$/gm, '<div class="md-h3">$1</div>');
  html = html.replace(/^## (.+)$/gm, '<div class="md-h2">$1</div>');
  html = html.replace(/^# (.+)$/gm, '<div class="md-h1">$1</div>');
  
  // Bold
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  
  // Italic
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
  
  // Tables
  html = html.replace(/\|(.+)\|\n\|[-:\s|]+\|\n((?:\|.+\|\n?)+)/g, (match, header, body) => {
    const headers = header.split('|').map(h => h.trim()).filter(h => h);
    const rows = body.trim().split('\n').map(row => 
      row.split('|').map(cell => cell.trim()).filter(c => c)
    );
    
    let table = '<table><thead><tr>';
    headers.forEach(h => table += `<th>${h}</th>`);
    table += '</tr></thead><tbody>';
    rows.forEach(row => {
      table += '<tr>';
      row.forEach(cell => table += `<td>${cell}</td>`);
      table += '</tr>';
    });
    table += '</tbody></table>';
    return table;
  });
  
  // Lists
  const lines = html.split('\n');
  let inList = false;
  let listType = '';
  let result = [];
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const bulletMatch = line.match(/^[-•]\s+(.+)$/);
    const numberMatch = line.match(/^\d+[.)]\s+(.+)$/);
    
    if (bulletMatch) {
      if (!inList || listType !== 'ul') {
        if (inList) result.push(`</${listType}>`);
        result.push('<ul>');
        inList = true;
        listType = 'ul';
      }
      result.push(`<li>${bulletMatch[1]}</li>`);
    } else if (numberMatch) {
      if (!inList || listType !== 'ol') {
        if (inList) result.push(`</${listType}>`);
        result.push('<ol>');
        inList = true;
        listType = 'ol';
      }
      result.push(`<li>${numberMatch[1]}</li>`);
    } else {
      if (inList) {
        result.push(`</${listType}>`);
        inList = false;
        listType = '';
      }
      if (line.trim()) {
        result.push(`<p>${line}</p>`);
      }
    }
  }
  
  if (inList) {
    result.push(`</${listType}>`);
  }
  
  return result.join('');
};

const formatDate = (date) => {
  const now = new Date();
  const d = new Date(date);
  const diffDays = Math.floor((now - d) / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return 'This Week';
  if (diffDays < 30) return 'This Month';
  return 'Older';
};

const groupConversations = (conversations) => {
  const groups = {};
  conversations.forEach(conv => {
    const group = formatDate(conv.updatedAt || conv.createdAt);
    if (!groups[group]) groups[group] = [];
    groups[group].push(conv);
  });
  return groups;
};

// ============================================
// SUGGESTIONS DATA
// ============================================

const SUGGESTIONS = [
  {
    title: "Customer Overview",
    description: "How many active customers do I have and what's their status breakdown?",
    query: "How many active customers do I have? Show me the breakdown by status."
  },
  {
    title: "Expiring Documents",
    description: "Which customers have visas or documents expiring soon?",
    query: "Show me customers with visas or documents expiring in the next 30 days."
  },
  {
    title: "Form Completion",
    description: "Who hasn't completed their required forms?",
    query: "Which customers have incomplete forms that need attention?"
  },
  {
    title: "Recent Activity",
    description: "What happened with my customers this week?",
    query: "Give me a summary of customer activity from this week."
  }
];

// ============================================
// MAIN COMPONENT
// ============================================

const CoPilotPage = () => {
  const authenticatedAxios = useAuthenticatedAxios();
  const toast = useToast();
  const showToast = toast?.showToast || (() => {});
  
  // State
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showActivityLog, setShowActivityLog] = useState(true);
  
  // Refs
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  
  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Load conversations on mount
  useEffect(() => {
    loadConversations();
  }, []);
  
  // Load a conversation when selected
  useEffect(() => {
    if (activeConversationId) {
      loadConversation(activeConversationId);
    } else {
      setMessages([]);
    }
  }, [activeConversationId]);
  
  // API Functions
  const loadConversations = async () => {
    try {
      const response = await authenticatedAxios.get('/copilot/conversations');
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    }
  };
  
  const loadConversation = async (conversationId) => {
    try {
      const response = await authenticatedAxios.get(`/copilot/conversations/${conversationId}`);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Failed to load conversation:', error);
      showToast('Failed to load conversation', 'error');
    }
  };
  
  const createNewConversation = async () => {
    setActiveConversationId(null);
    setMessages([]);
    setInput('');
    textareaRef.current?.focus();
  };
  
  const deleteConversation = async (conversationId, e) => {
    e.stopPropagation();
    try {
      await authenticatedAxios.delete(`/copilot/conversations/${conversationId}`);
      setConversations(prev => prev.filter(c => c._id !== conversationId));
      if (activeConversationId === conversationId) {
        setActiveConversationId(null);
        setMessages([]);
      }
      showToast('Conversation deleted', 'success');
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      showToast('Failed to delete conversation', 'error');
    }
  };
  
  const sendMessage = useCallback(async (messageText = null) => {
    const text = messageText || input.trim();
    if (!text || isLoading) return;
    
    // Optimistically add user message
    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: text,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    
    try {
      const response = await authenticatedAxios.post('/copilot/message', {
        message: text,
        conversationId: activeConversationId,
        conversationHistory: messages.slice(-20)
      });
      
      const assistantMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: response.data.message,
        timestamp: new Date().toISOString()
      };
      
      setMessages(prev => [...prev, assistantMessage]);
      
      // Update conversation ID if it's a new conversation
      if (response.data.conversationId && !activeConversationId) {
        setActiveConversationId(response.data.conversationId);
        loadConversations(); // Refresh the list
      }
      
    } catch (error) {
      console.error('Failed to send message:', error);
      
      const errorMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: "I'm having trouble connecting right now. Please try again in a moment.",
        timestamp: new Date().toISOString(),
        isError: true
      };
      
      setMessages(prev => [...prev, errorMessage]);
      showToast('Failed to get response', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, activeConversationId, messages, authenticatedAxios, showToast]);
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };
  
  const handleInputChange = (e) => {
    setInput(e.target.value);
    // Auto-resize textarea
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 200) + 'px';
  };
  
  const copyToClipboard = (text, messageId) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000);
    });
  };
  
  const regenerateResponse = async (messageIndex) => {
    // Find the user message before this assistant message
    const userMessageIndex = messageIndex - 1;
    if (userMessageIndex >= 0 && messages[userMessageIndex]?.role === 'user') {
      // Remove messages from this point onwards
      setMessages(prev => prev.slice(0, userMessageIndex + 1));
      // Resend the user message
      const userMessage = messages[userMessageIndex].content;
      setInput(userMessage);
      setTimeout(() => sendMessage(userMessage), 100);
    }
  };
  
  // Group conversations for sidebar
  const groupedConversations = groupConversations(conversations);
  
  return (
    <PageContainer>
      {/* Sidebar with conversation history */}
      <Sidebar $collapsed={sidebarCollapsed}>
        <SidebarHeader>
          <NewChatButton onClick={createNewConversation}>
            <BsPlusLg />
            New Chat
          </NewChatButton>
        </SidebarHeader>
        
        <ConversationList>
          {Object.entries(groupedConversations).map(([group, convs]) => (
            <ConversationGroup key={group}>
              <GroupLabel>{group}</GroupLabel>
              {convs.map(conv => (
                <ConversationItem 
                  key={conv._id}
                  $active={activeConversationId === conv._id}
                  onClick={() => setActiveConversationId(conv._id)}
                >
                  <BsChatDots className="icon" />
                  <span className="title">{conv.title || 'New conversation'}</span>
                  <BsTrash 
                    className="delete-btn" 
                    onClick={(e) => deleteConversation(conv._id, e)}
                  />
                </ConversationItem>
              ))}
            </ConversationGroup>
          ))}
          
          {conversations.length === 0 && (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px 20px', 
              color: 'rgba(165, 180, 252, 0.4)',
              fontSize: '13px'
            }}>
              No conversations yet.<br />
              Start a new chat!
            </div>
          )}
        </ConversationList>
      </Sidebar>
      
      {/* Main chat area */}
      <MainArea>
        <ChatHeader>
          <HeaderLeft>
            <div className="icon-wrapper">
              <img src={CoPilotIcon} alt="Co-Pilot" style={{ filter: 'brightness(0) saturate(100%) invert(67%) sepia(74%) saturate(1000%) hue-rotate(360deg) brightness(103%) contrast(101%)' }} />
            </div>
            <div className="title-text">
              <h2>Co-Pilot</h2>
              <span>Your AI assistant for customer insights</span>
            </div>
          </HeaderLeft>
          <ToggleLogButton 
            $active={showActivityLog}
            onClick={() => setShowActivityLog(!showActivityLog)}
          >
            <BsLayoutSidebarReverse />
            {showActivityLog ? 'Hide' : 'Show'} Activity Log
          </ToggleLogButton>
        </ChatHeader>
        
        <MessagesArea>
          {messages.length === 0 ? (
            <WelcomeContainer>
              <WelcomeIcon>
                <img src={CoPilotIcon} alt="Co-Pilot" style={{ filter: 'brightness(0) saturate(100%) invert(67%) sepia(74%) saturate(1000%) hue-rotate(360deg) brightness(103%) contrast(101%)' }} />
              </WelcomeIcon>
              <WelcomeTitle>
                Welcome to <span>Co-Pilot</span>
              </WelcomeTitle>
              <WelcomeSubtitle>
                I know everything about your customers - their status, documents, forms, 
                conversations, and more. Ask me anything about your business data.
              </WelcomeSubtitle>
              
              <SuggestionsGrid>
                {SUGGESTIONS.map((suggestion, idx) => (
                  <SuggestionCard 
                    key={idx}
                    onClick={() => sendMessage(suggestion.query)}
                  >
                    <div className="title">{suggestion.title}</div>
                    <div className="description">{suggestion.description}</div>
                  </SuggestionCard>
                ))}
              </SuggestionsGrid>
            </WelcomeContainer>
          ) : (
            <>
              {messages.map((msg, idx) => (
                <MessageContainer key={msg.id || idx}>
                  <MessageRow $isUser={msg.role === 'user'}>
                    <Avatar $isUser={msg.role === 'user'}>
                      {msg.role === 'user' ? 'U' : <img src={CoPilotIcon} alt="" style={{ filter: 'brightness(0) saturate(100%) invert(67%) sepia(74%) saturate(1000%) hue-rotate(360deg) brightness(103%) contrast(101%)' }} />}
                    </Avatar>
                    <MessageContent>
                      <MessageBubble 
                        $isUser={msg.role === 'user'}
                        dangerouslySetInnerHTML={{ 
                          __html: DOMPurify.sanitize(
                            msg.role === 'user' ? msg.content : parseMarkdown(msg.content)
                          ) 
                        }}
                      />
                      {msg.role === 'assistant' && (
                        <MessageActions>
                          <ActionButton 
                            className={copiedMessageId === msg.id ? 'copied' : ''}
                            onClick={() => copyToClipboard(msg.content, msg.id)}
                          >
                            {copiedMessageId === msg.id ? <BsCheck2 /> : <BsClipboard />}
                            {copiedMessageId === msg.id ? 'Copied!' : 'Copy'}
                          </ActionButton>
                          <ActionButton onClick={() => regenerateResponse(idx)}>
                            <BsArrowClockwise />
                            Regenerate
                          </ActionButton>
                        </MessageActions>
                      )}
                    </MessageContent>
                  </MessageRow>
                </MessageContainer>
              ))}
              
              {isLoading && (
                <MessageContainer>
                  <MessageRow>
                    <Avatar>
                      <img src={CoPilotIcon} alt="" style={{ filter: 'brightness(0) saturate(100%) invert(67%) sepia(74%) saturate(1000%) hue-rotate(360deg) brightness(103%) contrast(101%)' }} />
                    </Avatar>
                    <MessageContent>
                      <TypingIndicator>
                        <span />
                        <span />
                        <span />
                      </TypingIndicator>
                    </MessageContent>
                  </MessageRow>
                </MessageContainer>
              )}
            </>
          )}
          <div ref={messagesEndRef} />
        </MessagesArea>
        
        <InputArea>
          <InputContainer>
            <InputWrapper>
              <TextArea
                ref={textareaRef}
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="Ask about your customers, cases, documents, or any business data..."
                disabled={isLoading}
                rows={1}
              />
              <SendButton 
                onClick={() => sendMessage()}
                disabled={!input.trim() || isLoading}
              >
                ↑
              </SendButton>
            </InputWrapper>
          </InputContainer>
          <InputHint>
            Press Enter to send, Shift+Enter for new line
          </InputHint>
        </InputArea>
      </MainArea>
      
      {/* Activity Log Panel */}
      <ActivityLogPanel $collapsed={!showActivityLog}>
        <ActivityLog maxItems={500} autoRefresh={true} refreshInterval={30000} />
      </ActivityLogPanel>
    </PageContainer>
  );
};

export default CoPilotPage;
